import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ToggleOptionCard } from './toggleOptionCard';
import Loader from 'components/v2/common/loader';

import api from 'api';
import {
    computeFeatureChanges,
    featureDescriptionKey,
    formatFeatureName,
    formatRetainerFlowName,
    isRetainerFlowFeature,
    retainerFlowDescriptionKey,
    selectedRetainerFlow,
    splitAvailableFeatures,
} from 'helpers/serviceProviderFeatures';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { ServiceProviderAdminRow } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface ServiceProviderFeaturesDialogProps {
    provider: ServiceProviderAdminRow;
    onClose: () => void;
    onUpdated: (providerId: number, enabledFeatures: string[]) => void;
}

const DIALOG_WIDTH = 630;
const RETAINER_FLOW_SELECT_WIDTH = 448;

export function ServiceProviderFeaturesDialog({
    provider,
    onClose,
    onUpdated,
}: ServiceProviderFeaturesDialogProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [enabledFeatures, setEnabledFeatures] = useState<string[]>([]);
    const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const response = await api.serviceProvider.getFeatures(provider.id);
                if (cancelled) return;
                setEnabledFeatures(response.data.enabled_features);
                setAvailableFeatures(Array.from(new Set(response.data.available_features)).sort());
                setSelected(response.data.enabled_features);
            } catch (error) {
                if (cancelled) return;
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, provider.id, translate]);

    const { features, retainerFlows } = useMemo(
        () => splitAvailableFeatures(availableFeatures),
        [availableFeatures]
    );
    const changes = computeFeatureChanges(enabledFeatures, selected);
    const hasChanges = changes.add.length + changes.remove.length > 0;
    const currentFlow = selectedRetainerFlow(selected);
    const flowDescriptionKey = retainerFlowDescriptionKey(currentFlow);

    const handleToggle = (name: string) => {
        setSelected((prev) =>
            prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
        );
    };

    const handleFlowChange = (flow: string) => {
        setSelected((prev) => [
            ...prev.filter((item) => !isRetainerFlowFeature(item)),
            ...(flow ? [flow] : []),
        ]);
    };

    const handleClose = () => {
        if (!isSaving) onClose();
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const response = await api.serviceProvider.updateFeatures(provider.id, changes);
            dispatch(setAlert({ message: translate('featuresUpdated'), type: 'success' }));
            onUpdated(provider.id, response.data.enabled_features);
            onClose();
        } catch (error) {
            setIsSaving(false);
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth={false}
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: fullScreen ? 0 : 2,
                        maxHeight: fullScreen ? '100%' : '90vh',
                        width: fullScreen ? '100%' : `min(${DIALOG_WIDTH}px, calc(100% - 64px))`,
                        maxWidth: fullScreen ? '100%' : DIALOG_WIDTH,
                    },
                },
            }}
        >
            <DialogTitle sx={{ py: 2 }}>
                <Typography
                    variant="h6"
                    component="span"
                    sx={{ fontWeight: 700, display: 'block' }}
                >
                    {translate('manageFeatures')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {provider.company_name}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading ? (
                    <Loader isCircular />
                ) : (
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                {translate('features')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 1.25,
                                    alignItems: 'stretch',
                                }}
                            >
                                {features.map((name) => {
                                    const descriptionKey = featureDescriptionKey(name);

                                    return (
                                        <ToggleOptionCard
                                            key={name}
                                            title={formatFeatureName(name)}
                                            description={
                                                descriptionKey
                                                    ? translate(descriptionKey)
                                                    : undefined
                                            }
                                            selected={selected.includes(name)}
                                            disabled={isSaving}
                                            onToggle={() => handleToggle(name)}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <FormControl
                            disabled={isSaving}
                            sx={{
                                width: { xs: '100%', sm: RETAINER_FLOW_SELECT_WIDTH },
                                maxWidth: '100%',
                            }}
                        >
                            <InputLabel id="retainer-flow-label">
                                {translate('retainerFlow')}
                            </InputLabel>
                            <Select
                                labelId="retainer-flow-label"
                                label={translate('retainerFlow')}
                                value={currentFlow ?? ''}
                                onChange={(event) => handleFlowChange(String(event.target.value))}
                                sx={{
                                    '& .MuiSelect-select': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            >
                                <MenuItem value="">{translate('noRetainerFlow')}</MenuItem>
                                {retainerFlows.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {formatRetainerFlowName(name)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {flowDescriptionKey ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: -1, lineHeight: 1.45 }}
                            >
                                {translate(flowDescriptionKey)}
                            </Typography>
                        ) : null}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSaving}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={!hasChanges || isSaving}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ServiceProviderFeaturesDialog;
